import React, { useState } from "react";
import styled from "styled-components";

interface GroupTypeSelectPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedGroupId: string) => void;
  selectedValue?: string;
}

export default function GroupTypeSelectPopup({
  isOpen,
  onClose,
  onSelect,
  selectedValue,
}: GroupTypeSelectPopupProps) {
  const [selectedGroupId, setSelectedGroupId] = useState(selectedValue || "");

  const groupOptions = [
    { id: "1", name: "연습장 동호회" },
    { id: "2", name: "학교 동문회" },
    { id: "3", name: "지역 동호회" },
    { id: "4", name: "온라인 동호회(밴드,카페 등)" },
    { id: "5", name: "기업 동호회" },
    { id: "6", name: "골프 업종 모임" },
    { id: "7", name: "친목 모임" },
    { id: "8", name: "기타 모임" },
  ];

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  const handleConfirm = () => {
    onSelect(selectedGroupId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalContent>
          <ModalHeader>
            <GroupTitle>단체 구분</GroupTitle>
          </ModalHeader>

          <OptionsContainer>
            {groupOptions.map((option) => (
              <OptionButton
                key={option.id}
                isSelected={selectedGroupId === option.id}
                onClick={() => handleGroupSelect(option.id)}
              >
                <OptionContent>
                  <CheckIcon isActive={selectedGroupId === option.id}>
                    {selectedGroupId === option.id && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </CheckIcon>
                  <OptionText>{option.name}</OptionText>
                </OptionContent>
              </OptionButton>
            ))}
          </OptionsContainer>
        </ModalContent>

        <ModalButtons>
          <CancelButton onClick={onClose}>뒤로</CancelButton>
          <SelectButton onClick={handleConfirm}>확인</SelectButton>
        </ModalButtons>
      </ModalContainer>
    </ModalOverlay>
  );
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(1, 1, 1, 0.86);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
  animation: fade-in 0.2s;

  @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  max-width: 800px;
  width: calc(100% - 32px);
  height: auto;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  background: #fdfdfd;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0;
`;

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  flex: 1;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GroupTitle = styled.h2`
  align-self: stretch;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const OptionButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  min-width: 220px;
  flex: 1;
  height: 40px;
  justify-content: flex-start;
  align-items: center;
  border-radius: 6px;
  background: #f8f9fa;
  border: 1px solid ${(props) => (props.isSelected ? "#007BFF" : "#E5E7EB")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f4;
  }
`;

const OptionContent = styled.div`
  display: flex;
  padding: 8px 10px;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const CheckIcon = styled.div<{ isActive: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${(props) => (props.isActive ? "#007BFF" : "#D1D5D8")};
  color: white;
`;

const OptionText = styled.span`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-left: 10px;
`;

const ModalButtons = styled.div`
  display: flex;
  height: 60px;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  width: 100%;
`;

const CancelButton = styled.button`
  display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 0 6px;
  background: #9ca3af;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
`;

const SelectButton = styled.button`
  display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 6px 0;
  background: #004b49;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
`;
